import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "checklistArrayField", "equipArrayField", "inspectionPointsField",
    "inspectionImage", "inspectionPointsContainer",
    "fuelDepartureSlider", "fuelDepartureBar", "fuelDepartureLabel",
    "fuelArrivalSlider", "fuelArrivalBar", "fuelArrivalLabel"
  ]

  static values = { customData: Object }

  connect() {
    this._inspectionPoints = []
    this._pointCounter = 0

    // Listen for serialize event from report-form controller
    this.element.closest("[data-report-form-target='cdvFields']")
      ?.addEventListener("cdv:serialize", () => this.serializeData())

    if (this.customDataValue && Object.keys(this.customDataValue).length > 0) {
      this._populateEditData()
    }
  }

  // ── Inspection Points ──────────────────────────────────────────────

  addInspectionPoint(e) {
    // Ignore clicks on existing point spans (those trigger removePoint instead)
    if (e.target.closest("[data-point-index]")) return

    const container = this.inspectionImageTarget
    const rect = container.getBoundingClientRect()
    const xRatio = (e.clientX - rect.left) / rect.width
    const yRatio = (e.clientY - rect.top) / rect.height

    this._pointCounter++
    const num = this._pointCounter
    this._inspectionPoints.push([num, xRatio, yRatio])

    this._renderPoint(num, xRatio, yRatio)
  }

  removeLastPoint() {
    if (this._inspectionPoints.length === 0) return
    this._inspectionPoints.pop()
    this._pointCounter = this._inspectionPoints.length > 0
      ? this._inspectionPoints[this._inspectionPoints.length - 1][0]
      : 0
    this._renderAllPoints()
  }

  removePoint(e) {
    const idx = parseInt(e.currentTarget.dataset.pointIndex)
    this._inspectionPoints.splice(idx, 1)
    // Renumber
    this._inspectionPoints.forEach((p, i) => { p[0] = i + 1 })
    this._pointCounter = this._inspectionPoints.length
    this._renderAllPoints()
  }

  _renderPoint(num, xRatio, yRatio) {
    const container = this.inspectionPointsContainerTarget
    const span = this._buildPointSpan(num, xRatio, yRatio, this._inspectionPoints.length - 1)
    container.appendChild(span)
  }

  _renderAllPoints() {
    const container = this.inspectionPointsContainerTarget
    container.querySelectorAll("span").forEach(s => s.remove())
    this._inspectionPoints.forEach(([num, x, y], idx) => {
      container.appendChild(this._buildPointSpan(num, x, y, idx))
    })
  }

  _buildPointSpan(num, x, y, idx) {
    const span = document.createElement("span")
    span.className = "absolute flex items-center justify-center w-6 h-6 rounded-full border-2 border-blue-500 bg-white/90 text-blue-600 text-xs font-bold cursor-pointer -translate-x-1/2 -translate-y-1/2 hover:bg-red-50 hover:border-red-500 hover:text-red-600 select-none pointer-events-auto"
    span.style.left = `${x * 100}%`
    span.style.top = `${y * 100}%`
    span.textContent = num
    span.dataset.pointIndex = idx
    span.dataset.action = "click->cdv-form#removePoint"
    return span
  }

  // ── Fuel Sliders ───────────────────────────────────────────────────

  updateDepartureSlider() {
    const val = this.fuelDepartureSliderTarget.value
    this.fuelDepartureBarTarget.style.width = `${val}%`
    this.fuelDepartureLabelTarget.textContent = `${val}%`
  }

  updateArrivalSlider() {
    const val = this.fuelArrivalSliderTarget.value
    this.fuelArrivalBarTarget.style.width = `${val}%`
    this.fuelArrivalLabelTarget.textContent = `${val}%`
  }

  // ── Serialization ──────────────────────────────────────────────────

  serializeData() {
    // Checklist (24 items)
    const checklistArr = []
    for (let i = 0; i < 24; i++) {
      const radios = this.element.querySelectorAll(`input[name="checklist_item_${i}"]`)
      let val = ""
      radios.forEach(r => { if (r.checked) val = r.value })
      checklistArr.push(val)
    }
    this.checklistArrayFieldTarget.value = JSON.stringify(checklistArr)

    // Equipment (15 items)
    const equipArr = []
    for (let i = 0; i < 15; i++) {
      const radios = this.element.querySelectorAll(`input[name="equip_item_${i}"]`)
      let val = ""
      radios.forEach(r => { if (r.checked) val = r.value })
      equipArr.push(val)
    }
    this.equipArrayFieldTarget.value = JSON.stringify(equipArr)

    // Inspection points
    this.inspectionPointsFieldTarget.value = JSON.stringify(this._inspectionPoints)
  }

  // ── Edit Mode Population ───────────────────────────────────────────

  _populateEditData() {
    const d = this.customDataValue

    // Inspection points
    if (d.inspection_points && Array.isArray(d.inspection_points)) {
      this._inspectionPoints = d.inspection_points.map(p => [p[0], p[1], p[2]])
      this._pointCounter = this._inspectionPoints.length
      this._renderAllPoints()
    }

    // Checklist radios
    if (d.checklist_array && Array.isArray(d.checklist_array)) {
      d.checklist_array.forEach((val, i) => {
        if (val) {
          const radio = this.element.querySelector(`input[name="checklist_item_${i}"][value="${val}"]`)
          if (radio) radio.checked = true
        }
      })
    }

    // Equipment radios
    if (d.equip_array && Array.isArray(d.equip_array)) {
      d.equip_array.forEach((val, i) => {
        if (val) {
          const radio = this.element.querySelector(`input[name="equip_item_${i}"][value="${val}"]`)
          if (radio) radio.checked = true
        }
      })
    }

    // Tipo da solicitação
    if (d.tipo_da_solicitacao) {
      const radio = this.element.querySelector(`input[name="cdv_data[tipo_da_solicitacao]"][value="${d.tipo_da_solicitacao}"]`)
      if (radio) radio.checked = true
    }

    // Fuel sliders
    if (d.saida_gas != null && this.hasFuelDepartureSliderTarget) {
      this.fuelDepartureSliderTarget.value = d.saida_gas
      this.updateDepartureSlider()
    }
    if (d.chegada_gas != null && this.hasFuelArrivalSliderTarget) {
      this.fuelArrivalSliderTarget.value = d.chegada_gas
      this.updateArrivalSlider()
    }
  }
}
